import React, { useState } from 'react';
import { ArrowLeft, MapPin, Star, Sparkles, Plus, Tag, Trash2 } from 'lucide-react';
import { CafeList, SavedCafe, Store } from '../../types';
import { STORES } from '../../data/mockData';
import { useToast } from '../ToastContext';
interface ListDetailScreenProps {
  list: CafeList;
  onBack: () => void;
  onMixAndShare: () => void;
}
const storeById = (id: string): Store | undefined => STORES.find(s => s.id === id);
export function ListDetailScreen({
  list,
  onBack,
  onMixAndShare
}: ListDetailScreenProps) {
  const [cafes, setCafes] = useState<SavedCafe[]>(list.cafes);
  const [editingVibe, setEditingVibe] = useState<string | null>(null);
  const [vibeInput, setVibeInput] = useState('');
  const {
    showToast
  } = useToast();
  const handleSaveVibe = (storeId: string) => {
    setCafes(prev => prev.map(c => c.storeId === storeId ? {
      ...c,
      vibe: vibeInput.trim() || undefined
    } : c));
    setEditingVibe(null);
    setVibeInput('');
    showToast('Vibe saved', 'success');
  };
  const handleRemove = (storeId: string) => {
    setCafes(prev => prev.filter(c => c.storeId !== storeId));
    showToast('Removed from list', 'info');
  };
  return <div className="flex flex-col h-full bg-white pb-24 overflow-y-auto" data-id="element-1267">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100" data-id="element-1268">
        <div className="flex items-center gap-3 px-4 py-4" data-id="element-1269">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1270">
            <ArrowLeft size={22} data-id="element-1271" />
          </button>
          <div className="flex-1 min-w-0" data-id="element-1272">
            <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2 truncate" data-id="element-1273">
              <span data-id="element-1274">{list.emoji || '📌'}</span>
              <span className="truncate" data-id="element-1275">{list.name}</span>
            </h1>
            <p className="text-xs text-gray-500" data-id="element-1276">
              {cafes.length} {cafes.length === 1 ? 'place' : 'places'}
              {list.type === 'been' && ' · auto-updated from check-ins'}
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 pt-4" data-id="element-1277">
        <button onClick={onMixAndShare} className="w-full flex items-center justify-center gap-2 py-3 bg-[#FFFC00] text-black rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-sm" data-id="element-1278">
          <Sparkles size={16} data-id="element-1279" />
          Mix & Share these places
        </button>
      </div>

      {/* Cafes */}
      <div className="px-4 py-4 space-y-3" data-id="element-1280">
        {cafes.length === 0 ? <div className="text-center py-16 text-gray-400" data-id="element-1281">
            <Plus size={32} className="mx-auto mb-2 opacity-50" data-id="element-1282" />
            <p className="text-sm" data-id="element-1283">No cafes in this list yet</p>
          </div> : cafes.map(cafe => {
        const store = storeById(cafe.storeId);
        if (!store) return null;
        return <div key={cafe.storeId} className="bg-white border border-gray-200 rounded-2xl p-3 flex gap-3" data-id="element-1284">
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100" data-id="element-1285">
                  <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-1286" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1287">
                  <div className="flex items-start justify-between gap-2" data-id="element-1288">
                    <h3 className="font-bold text-gray-900 truncate" data-id="element-1289">
                      {store.name}
                    </h3>
                    {list.type !== 'been' && <button onClick={() => handleRemove(cafe.storeId)} aria-label={`Remove ${store.name} from list`} className="flex-shrink-0 w-8 h-8 bg-[#FFFC00] hover:bg-red-100 rounded-full flex items-center justify-center shadow-sm active:scale-90 transition-all group" style={{
                transform: 'rotate(6deg)'
              }} data-id="element-1290">
                        <Trash2 size={14} className="text-black group-hover:text-red-500 transition-colors" strokeWidth={2.5} data-id="element-1291" />
                      </button>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5" data-id="element-1292">
                    <span className="flex items-center gap-0.5" data-id="element-1293">
                      <Star size={10} className="text-amber-400 fill-amber-400" data-id="element-1294" />
                      {store.rating}
                    </span>
                    <span data-id="element-1295">·</span>
                    <span className="flex items-center gap-0.5" data-id="element-1296">
                      <MapPin size={10} data-id="element-1297" />
                      {store.distance}
                    </span>
                    {cafe.visitCount && <>
                        <span data-id="element-1298">·</span>
                        <span data-id="element-1299">{cafe.visitCount} visits</span>
                      </>}
                  </div>

                  {/* Vibe tag */}
                  {editingVibe === cafe.storeId ? <div className="mt-2 flex gap-1" data-id="element-1300">
                      <input autoFocus type="text" value={vibeInput} onChange={e => setVibeInput(e.target.value)} placeholder="e.g. Best for rainy days" maxLength={40} className="flex-1 px-2 py-1 text-xs rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none" data-id="element-1301" />
                      <button onClick={() => handleSaveVibe(cafe.storeId)} className="px-2 py-1 bg-black text-[#FFFC00] rounded-lg text-xs font-semibold" data-id="element-1302">
                        Save
                      </button>
                    </div> : cafe.vibe ? <button onClick={() => {
              setEditingVibe(cafe.storeId);
              setVibeInput(cafe.vibe || '');
            }} className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[11px] font-medium hover:bg-purple-100 transition-colors" data-id="element-1303">
                      <Tag size={9} data-id="element-1304" />
                      {cafe.vibe}
                    </button> : <button onClick={() => {
              setEditingVibe(cafe.storeId);
              setVibeInput('');
            }} className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1305">
                      <Plus size={10} data-id="element-1306" />
                      Add a vibe
                    </button>}
                </div>
              </div>;
      })}
      </div>
    </div>;
}